import React, { useState, useMemo } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { Badge } from '../../../components/ui/Badge'
import { 
  Search, 
  ChevronDown, 
  ChevronUp, 
  Star, 
  TrendingUp,
  Filter,
  CheckCircle,
  Users,
  Target,
  Plus,
  Car,
  Building2,
  Truck,
  Ship,
  Factory,
  Briefcase,
  Zap,
  Cpu,
  Heart,
  ShoppingBag
} from 'lucide-react'
import { IndustryConfig, IndustryType } from '../../../types'
import { cn } from '../../../lib/utils'
import { CustomIndustryRequestModal, CustomIndustryRequestData } from './CustomIndustryRequestModal'

// Enhanced industry data with scalability metadata
interface EnhancedIndustryData {
  config: IndustryConfig
  isPopular: boolean
  isEmerging: boolean
  category: 'manufacturing' | 'services' | 'technology' | 'healthcare' | 'other'
  matchingCompanies: number
  searchKeywords: string[]
}

// Icon mapping for industries (expandable)
const industryIcons = {
  'automotive-services': Car,
  'construction-equipment': Building2,
  'heavy-equipment': Truck,
  'transportation-logistics': Ship,
  'manufacturing': Factory,
  'professional-services': Briefcase,
  'technology': Cpu,
  'healthcare': Heart,
  'retail': ShoppingBag,
  'energy': Zap,
} as const

// Enhanced industry metadata (this would come from your database)
const industryMetadata: Record<string, Omit<EnhancedIndustryData, 'config'>> = {
  'automotive-services': {
    isPopular: true,
    isEmerging: false,
    category: 'manufacturing',
    matchingCompanies: 847,
    searchKeywords: ['automotive', 'auto', 'vehicle', 'car', 'repair', 'service', 'maintenance']
  },
  'construction-equipment': {
    isPopular: true,
    isEmerging: false,
    category: 'manufacturing',
    matchingCompanies: 623,
    searchKeywords: ['construction', 'building', 'equipment', 'machinery', 'infrastructure']
  },
  'heavy-equipment': {
    isPopular: false,
    isEmerging: false,
    category: 'manufacturing',
    matchingCompanies: 445,
    searchKeywords: ['heavy', 'equipment', 'machinery', 'industrial', 'mining']
  },
  'transportation-logistics': {
    isPopular: true,
    isEmerging: false,
    category: 'services',
    matchingCompanies: 1203,
    searchKeywords: ['transportation', 'logistics', 'shipping', 'freight', 'delivery', 'supply chain']
  },
  'manufacturing': {
    isPopular: true,
    isEmerging: false,
    category: 'manufacturing',
    matchingCompanies: 1567,
    searchKeywords: ['manufacturing', 'production', 'factory', 'industrial', 'assembly']
  },
  'professional-services': {
    isPopular: true,
    isEmerging: false,
    category: 'services',
    matchingCompanies: 892,
    searchKeywords: ['professional', 'services', 'consulting', 'advisory', 'business services']
  },
  // Future industries (these would be your additional 4+ industries)
  'technology': {
    isPopular: true,
    isEmerging: true,
    category: 'technology',
    matchingCompanies: 1834,
    searchKeywords: ['technology', 'tech', 'software', 'IT', 'digital', 'saas']
  },
  'healthcare': {
    isPopular: true,
    isEmerging: true,
    category: 'healthcare',
    matchingCompanies: 756,
    searchKeywords: ['healthcare', 'medical', 'pharmaceutical', 'biotech', 'health']
  },
  'retail': {
    isPopular: false,
    isEmerging: true,
    category: 'services',
    matchingCompanies: 1089,
    searchKeywords: ['retail', 'commerce', 'shopping', 'consumer', 'sales']
  },
  'energy': {
    isPopular: false,
    isEmerging: true,
    category: 'other',
    matchingCompanies: 234,
    searchKeywords: ['energy', 'power', 'utilities', 'renewable', 'oil', 'gas']
  }
}

interface ScalableIndustryGridProps {
  industries: IndustryConfig[]
  selectedIndustry: IndustryType | null
  onIndustrySelect: (industryId: IndustryType) => void
  onCustomIndustryRequest?: (requestData: CustomIndustryRequestData) => Promise<void>
  initialDisplayCount?: number
}

export const ScalableIndustryGrid: React.FC<ScalableIndustryGridProps> = ({
  industries,
  selectedIndustry,
  onIndustrySelect,
  onCustomIndustryRequest,
  initialDisplayCount = 6
}) => {
  const [showAll, setShowAll] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<'all' | 'popular' | 'emerging'>('all')
  const [isCustomRequestModalOpen, setIsCustomRequestModalOpen] = useState(false)

  // Enhance industries with metadata
  const enhancedIndustries: EnhancedIndustryData[] = useMemo(() => {
    return industries.map(config => ({
      config,
      ...industryMetadata[config.id] || {
        isPopular: false,
        isEmerging: false,
        category: 'other' as const,
        matchingCompanies: 0,
        searchKeywords: [config.name.toLowerCase()]
      }
    }))
  }, [industries])

  // Filter and search logic
  const filteredIndustries = useMemo(() => {
    let filtered = enhancedIndustries

    // Apply category filter
    if (filterType === 'popular') {
      filtered = filtered.filter(industry => industry.isPopular)
    } else if (filterType === 'emerging') {
      filtered = filtered.filter(industry => industry.isEmerging)
    }

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      filtered = filtered.filter(industry =>
        industry.config.name.toLowerCase().includes(searchLower) ||
        industry.config.description.toLowerCase().includes(searchLower) ||
        industry.searchKeywords.some(keyword => keyword.includes(searchLower))
      )
    }

    // Sort: popular first, then by company count
    return filtered.sort((a, b) => {
      if (a.isPopular && !b.isPopular) return -1
      if (!a.isPopular && b.isPopular) return 1
      return b.matchingCompanies - a.matchingCompanies
    })
  }, [enhancedIndustries, filterType, searchTerm])

  // Determine displayed industries
  const displayedIndustries = showAll 
    ? filteredIndustries 
    : filteredIndustries.slice(0, initialDisplayCount)

  const hiddenCount = filteredIndustries.length - initialDisplayCount
  const shouldShowToggle = filteredIndustries.length > initialDisplayCount && !searchTerm

  const getIconComponent = (industryId: string) => {
    return industryIcons[industryId as keyof typeof industryIcons] || Briefcase
  }

  const handleCustomIndustryRequest = async (requestData: CustomIndustryRequestData) => {
    if (onCustomIndustryRequest) {
      await onCustomIndustryRequest(requestData)
    } else {
      // Default implementation - you can customize this
      console.log('Custom Industry Request:', requestData)
      
      // Example integrations:
      // 1. Send to your API
      // await fetch('/api/custom-industry-request', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(requestData)
      // })
      
      // 2. Send email notification
      // await sendNotificationEmail(requestData)
      
      // 3. Store in database
      // await saveCustomIndustryRequest(requestData)
      
      // 4. Create support ticket
      // await createSupportTicket(requestData)
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000))
    }
  }

  return (
    <div className="space-y-6">
      {/* Search and Filter Header */}
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search industries (e.g., automotive, technology, healthcare...)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-12 text-lg"
          />
        </div>

        {/* Quick Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
            <Filter className="h-4 w-4" />
            Quick filters:
          </span>
          <Button
            variant={filterType === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('all')}
          >
            All Industries
          </Button>
          <Button
            variant={filterType === 'popular' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('popular')}
            className="flex items-center gap-1"
          >
            <Star className="h-3 w-3" />
            Popular
          </Button>
          <Button
            variant={filterType === 'emerging' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('emerging')}
            className="flex items-center gap-1"
          >
            <TrendingUp className="h-3 w-3" />
            Emerging
          </Button>
        </div>

        {/* Results summary */}
        <div className="text-sm text-gray-600">
          {searchTerm ? (
            <span>Found {filteredIndustries.length} industries matching "{searchTerm}"</span>
          ) : (
            <span>
              Showing {displayedIndustries.length} of {filteredIndustries.length} industries
              {filterType !== 'all' && ` (${filterType})`}
            </span>
          )}
        </div>
      </div>

      {/* Industry Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {displayedIndustries.map((industry) => {
          const isSelected = selectedIndustry === industry.config.id
          const IconComponent = getIconComponent(industry.config.id)
          
          return (
            <Card
              key={industry.config.id}
              className={cn(
                "group cursor-pointer transition-all duration-300 hover:shadow-xl border-2 relative overflow-hidden",
                isSelected 
                  ? "border-primary bg-primary/5 shadow-lg scale-105" 
                  : "border-gray-200 hover:border-primary/50 hover:scale-102"
              )}
              onClick={() => onIndustrySelect(industry.config.id)}
            >
              {/* Selection indicator */}
              {isSelected && (
                <div className="absolute top-4 right-4 z-10">
                  <CheckCircle className="w-6 h-6 text-primary fill-primary/20" />
                </div>
              )}

              {/* Gradient overlay for selected state */}
              {isSelected && (
                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent pointer-events-none" />
              )}

              <CardHeader className="text-center pb-6 relative">
                <div className={cn(
                  "w-20 h-20 mx-auto mb-6 rounded-2xl flex items-center justify-center transition-all duration-300",
                  isSelected 
                    ? "bg-primary text-white shadow-lg" 
                    : "bg-gray-100 text-gray-600 group-hover:bg-primary/10 group-hover:text-primary"
                )}>
                  <IconComponent className="w-10 h-10" />
                </div>
                
                {/* Industry Badges */}
                <div className="absolute top-4 left-4 flex gap-1">
                  {industry.isPopular && (
                    <Badge variant="secondary" className="text-xs px-2 py-0.5">
                      <Star className="h-3 w-3 mr-1" />
                      Popular
                    </Badge>
                  )}
                  {industry.isEmerging && (
                    <Badge variant="outline" className="text-xs px-2 py-0.5 border-green-200 text-green-700">
                      <TrendingUp className="h-3 w-3 mr-1" />
                      Emerging
                    </Badge>
                  )}
                </div>
                
                <CardTitle className="text-2xl mb-3 font-semibold">
                  {industry.config.name}
                </CardTitle>
                <CardDescription className="text-base text-gray-600 leading-relaxed">
                  {industry.config.description}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-6">
                {/* Business Types Section */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                    <Users className="w-4 h-4 text-primary" />
                    Business Types
                  </div>
                  <div className="space-y-2">
                    {industry.config.businessTypes.slice(0, 3).map((type) => (
                      <div key={type.id} className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                        <div>
                          <div className="text-sm font-medium text-gray-800">{type.name}</div>
                          <div className="text-xs text-gray-600">{type.description}</div>
                        </div>
                      </div>
                    ))}
                    {industry.config.businessTypes.length > 3 && (
                      <div className="text-xs text-gray-500 italic ml-5">
                        +{industry.config.businessTypes.length - 3} more specializations
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Intelligence Coverage & Social Proof */}
                <div className="pt-4 border-t border-gray-200 space-y-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                    <Target className="w-4 h-4 text-primary" />
                    Intelligence Coverage
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-lg font-bold text-primary">{industry.config.revenueStreamTypes.length}</div>
                      <div className="text-xs text-gray-600">Revenue Streams</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-lg font-bold text-primary">{industry.config.supplierCategories.length}</div>
                      <div className="text-xs text-gray-600">Supplier Categories</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-lg font-bold text-primary">{industry.config.competitiveFactors.length}</div>
                      <div className="text-xs text-gray-600">Competitive Factors</div>
                    </div>
                  </div>
                  
                  {/* Social Proof */}
                  {industry.matchingCompanies > 0 && (
                    <div className="text-center text-xs text-blue-600 bg-blue-50 rounded-lg p-2">
                      {industry.matchingCompanies.toLocaleString()}+ companies using our intelligence
                    </div>
                  )}
                </div>
                
                {/* Selection Status */}
                {isSelected && (
                  <div className="mt-6 p-4 bg-white rounded-xl border border-primary/20 shadow-sm">
                    <div className="flex items-center gap-2 text-primary font-semibold mb-2">
                      <CheckCircle className="w-4 h-4" />
                      Selected for Onboarding
                    </div>
                    <div className="text-sm text-gray-700">
                      Your intelligence setup will be customized for <strong>{industry.config.name.toLowerCase()}</strong> operations
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Show More/Less Toggle */}
      {shouldShowToggle && (
        <div className="text-center">
          <Button
            variant="outline"
            onClick={() => setShowAll(!showAll)}
            className="flex items-center gap-2 mx-auto px-6 py-3"
          >
            {showAll ? (
              <>
                <ChevronUp className="h-4 w-4" />
                Show Less Industries
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4" />
                Show {hiddenCount} More Industries
              </>
            )}
          </Button>
        </div>
      )}

      {/* No Results State */}
      {filteredIndustries.length === 0 && searchTerm && (
        <div className="text-center py-12">
          <div className="text-gray-500 mb-4">
            <Search className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p className="text-lg font-medium">No industries found</p>
            <p className="text-sm">Try adjusting your search terms or filters</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Button 
              variant="outline" 
              onClick={() => {
                setSearchTerm('')
                setFilterType('all')
              }}
            >
              Clear Search
            </Button>
            <span className="text-gray-400">or</span>
            <Button 
              variant="default"
              onClick={() => setIsCustomRequestModalOpen(true)}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Request Custom Industry
            </Button>
          </div>
        </div>
      )}

      {/* Custom Industry Option */}
      <Card className="border-dashed border-2 border-gray-300 hover:border-blue-400 transition-colors">
        <CardContent className="p-6 text-center">
          <div className="space-y-3">
            <div className="w-16 h-16 mx-auto bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
              <Plus className="w-8 h-8 text-gray-500" />
            </div>
            <h3 className="font-semibold text-gray-700">Don't see your industry?</h3>
            <p className="text-sm text-gray-600">
              We can create a custom intelligence configuration for your specific industry needs
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setIsCustomRequestModalOpen(true)}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Request Custom Industry
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Custom Industry Request Modal */}
      <CustomIndustryRequestModal
        isOpen={isCustomRequestModalOpen}
        onClose={() => setIsCustomRequestModalOpen(false)}
      />
    </div>
  )
}